import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import useEmailStore from "@store/useEmailStore";
import emailService from "@services/email.service";
import usePlatformStore from "@store/usePlatformStore";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { THREAD_QUERY_KEY, REMOVE_EMAIL_MUTATION_KEY, EMAIL_QUERY_KEY } from "@config/query";
import { IEmailResponse } from "@interfaces/responses.interface";

export function useRemoveEmail() {
  const { itemId, isComposeMode } = usePlatformStore();
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { threadId, emailId, setEmailId } = useEmailStore();

  return useMutation({
    mutationKey: [REMOVE_EMAIL_MUTATION_KEY],
    mutationFn: async () => {
      return await emailService.removeEmail(emailId);
    },
    onSuccess: (response) => {
      // Reset the emailId but keep the threadId so that we know about other synced emails in the thread
      setEmailId(null);
      queryClient.setQueryData([EMAIL_QUERY_KEY, itemId], (old: IEmailResponse) => ({
        ...old,
        id: null,
      }));

      queryClient.removeQueries({ queryKey: [THREAD_QUERY_KEY, threadId] });

      ToastService.dismiss("sync-when-sent");

      ToastService.showSuccess(
        isComposeMode ? "Email Won't Auto Sync" : "Email Successfully Removed"
      );
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.REMOVE_EMAIL);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.REMOVE_EMAIL);
    },
  });
}
